import {defineStore, storeToRefs} from 'pinia';
import {computed, ref} from 'vue';
import type {SketchDrawingTool} from '@puppycad/cad-core';
import {ViewerMode} from '@puppycad/cad-core';
import {useViewer} from './useViewer';
import {useSketchStateStore} from './useSketchState';

/**
 * 绘制状态全局 Store。
 * 职责：管理绘图模式、当前工具、绘制点计数及绘图提示。
 */
export const useDrawingStore = defineStore('drawing', () => {
    const {getViewer} = useViewer();
    const sketchStateStore = useSketchStateStore();
    const {viewerMode} = storeToRefs(sketchStateStore);

    /** 是否处于绘图模式。 */
    const isDrawingMode = computed(() => viewerMode.value === ViewerMode.SketchDrawing);

    /** 当前绘图工具 */
    const currentDrawingTool = ref<SketchDrawingTool | string | null>(null);

    /** 直线绘制点计数 */
    const lineDrawingPointCount = ref(0);

    /** 绘制点计数 */
    const drawingPointCount = ref(0);

    /** 绘图提示文本 */
    const drawingHint = ref('');

    /** 绘图提示定时器 ID */
    let drawingHintTimer: number | null = null;

    /**
     * 同步当前绘图提示文本。
     * 根据当前绘图工具从渲染器获取提示信息。
     */
    function syncDrawingHint(): void {
        const renderer = getViewer();
        if (!renderer || !isDrawingMode.value || !currentDrawingTool.value) {
            drawingHint.value = '';
            return;
        }
        drawingHint.value = renderer.resolveDrawingHintByTool(String(currentDrawingTool.value));
    }

    /**
     * 启动绘图提示定时刷新（100ms 间隔）。
     */
    function startDrawingHintTimer(): void {
        stopDrawingHintTimer();
        syncDrawingHint();
        drawingHintTimer = window.setInterval(() => {
            syncDrawingHint();
        }, 100);
    }

    /**
     * 停止绘图提示定时刷新并清空提示文本。
     */
    function stopDrawingHintTimer(): void {
        if (drawingHintTimer !== null) {
            clearInterval(drawingHintTimer);
            drawingHintTimer = null;
        }
        drawingHint.value = '';
    }

    /**
     * 重置草图绘制运行时状态。
     */
    function resetDrawingRuntimeState(): void {
        // 项目切换或 Viewer 重建时必须清掉旧工具，否则绘制按钮会沿用上一会话的半激活状态。
        stopDrawingHintTimer();
        currentDrawingTool.value = null;
        lineDrawingPointCount.value = 0;
        drawingPointCount.value = 0;
        drawingHint.value = '';
    }

    return {
        isDrawingMode,
        currentDrawingTool,
        lineDrawingPointCount,
        drawingPointCount,
        drawingHint,
        syncDrawingHint,
        startDrawingHintTimer,
        stopDrawingHintTimer,
        resetDrawingRuntimeState,
    };
});
